/**
 * Geometría del mapa mundial estilizado.
 *
 * **Proyección equirectangular**, elegida por una razón práctica: la conversión
 * de coordenadas es una regla de tres (`x = lon + 180`, `y = 90 − lat`), así que
 * el sistema de coordenadas del SVG *son* los grados. Las siluetas de los
 * continentes y las paradas del itinerario se proyectan con la misma función,
 * de modo que un marcador nunca puede caer en el mar por un desajuste entre dos
 * fuentes de coordenadas.
 *
 * Las siluetas son **deliberadamente low-poly**: es un mapa de cuento, no un
 * atlas. Menos vértices también significa un SVG que pesa poco y que se anima
 * sin coste en móviles modestos.
 */

/** Ancho del lienzo en unidades de longitud. */
export const MAP_WIDTH = 360

/** Alto del lienzo en unidades de latitud. */
export const MAP_HEIGHT = 180

/**
 * Recorte visible: de 94° N a 58° S. Deja sitio sobre el Polo Norte para el
 * halo y la aurora, y se come la Antártida, por la que el trineo no pasa.
 */
export const MAP_VIEWBOX = { x: 0, y: -4, width: MAP_WIDTH, height: 152 } as const

export const MAP_VIEWBOX_ATTR = `${MAP_VIEWBOX.x} ${MAP_VIEWBOX.y} ${MAP_VIEWBOX.width} ${MAP_VIEWBOX.height}`

/** Longitud → coordenada X del lienzo. */
export function projectX(lon: number): number {
  return lon + 180
}

/** Latitud → coordenada Y del lienzo. */
export function projectY(lat: number): number {
  return 90 - lat
}

export function project(lon: number, lat: number): { x: number; y: number } {
  return { x: projectX(lon), y: projectY(lat) }
}

type Ring = readonly (readonly [lon: number, lat: number])[]

function toPath(ring: Ring): string {
  const points = ring.map(([lon, lat]) => `${projectX(lon).toFixed(1)},${projectY(lat).toFixed(1)}`)
  return `M${points.join('L')}Z`
}

const LANDMASSES: Record<string, Ring> = {
  northAmerica: [
    [-168, 65], [-164, 60], [-155, 58], [-145, 60], [-136, 58], [-130, 54], [-124, 48],
    [-124, 40], [-117, 32], [-110, 23], [-105, 20], [-97, 16], [-92, 15], [-84, 10],
    [-78, 9], [-82, 15], [-88, 21], [-94, 18], [-97, 26], [-92, 30], [-85, 30], [-81, 25],
    [-80, 32], [-76, 37], [-70, 43], [-66, 45], [-60, 47], [-55, 52], [-64, 60], [-78, 62],
    [-80, 70], [-95, 70], [-105, 68], [-115, 70], [-125, 70], [-140, 70], [-155, 71], [-165, 68],
  ],
  canadianArctic: [
    [-100, 73], [-90, 76], [-80, 78], [-68, 80], [-80, 74], [-95, 74], [-108, 76], [-118, 74],
  ],
  greenland: [
    [-45, 60], [-52, 66], [-56, 72], [-60, 76], [-50, 82], [-35, 83], [-22, 76], [-25, 70], [-38, 65],
  ],
  cuba: [[-84, 22], [-78, 21], [-74, 20], [-79, 22], [-83, 23]],
  southAmerica: [
    [-78, 8], [-72, 11], [-62, 10], [-52, 5], [-50, 0], [-44, -3], [-35, -6], [-39, -14],
    [-48, -25], [-53, -34], [-58, -38], [-62, -40], [-65, -45], [-68, -52], [-75, -53],
    [-73, -45], [-73, -37], [-71, -30], [-70, -20], [-76, -14], [-81, -6], [-80, 0], [-78, 4],
  ],
  eurasia: [
    [-9, 38], [-6, 36], [0, 39], [3, 42], [7, 44], [12, 45], [14, 42], [18, 40], [23, 38],
    [27, 37], [30, 31], [34, 31], [36, 36], [43, 40], [48, 38], [50, 30], [57, 25], [60, 25],
    [67, 25], [70, 22], [73, 20], [73, 15], [77, 8], [80, 10], [82, 17], [88, 21], [92, 21],
    [95, 16], [99, 10], [104, 1], [105, 10], [109, 11], [108, 18], [110, 21], [117, 23],
    [122, 30], [122, 37], [126, 40], [130, 43], [135, 45], [142, 46], [143, 54], [137, 56],
    [142, 60], [150, 60], [160, 61], [170, 66], [180, 66], [180, 70], [160, 70], [140, 73],
    [128, 73], [110, 76], [95, 78], [75, 73], [65, 70], [55, 68], [45, 66], [35, 68], [30, 70],
    [22, 70], [15, 68], [10, 64], [5, 62], [8, 58], [10, 55], [8, 54], [4, 53], [0, 49],
    [-2, 48], [-4, 48], [-1, 46], [-2, 43], [-9, 43],
  ],
  britishIsles: [[-5, 50], [-6, 53], [-5, 55], [-6, 58], [-3, 58], [-1, 56], [0, 53], [1, 51], [-4, 50]],
  ireland: [[-10, 52], [-10, 54], [-7, 55], [-6, 54], [-6, 52]],
  iceland: [[-24, 65], [-22, 66], [-14, 66], [-14, 64], [-21, 63]],
  africa: [
    [-17, 15], [-16, 21], [-10, 27], [-6, 36], [10, 37], [20, 32], [25, 32], [33, 31],
    [35, 24], [39, 15], [43, 11], [51, 12], [50, 3], [41, -2], [40, -10], [36, -18],
    [33, -26], [27, -34], [20, -35], [18, -30], [13, -22], [12, -15], [9, -1], [5, 4],
    [-4, 5], [-8, 4], [-13, 8], [-16, 12],
  ],
  madagascar: [[43, -12], [50, -15], [48, -25], [45, -25], [43, -20]],
  japan: [[130, 32], [132, 34], [136, 35], [140, 36], [142, 40], [141, 45], [140, 42], [137, 37], [133, 34], [130, 31]],
  philippines: [[120, 18], [122, 14], [126, 10], [124, 6], [121, 12], [119, 16]],
  sumatra: [[95, 5], [100, 0], [104, -6], [102, -6], [97, 2]],
  java: [[105, -6], [114, -8], [112, -8], [105, -7]],
  borneo: [[109, 2], [117, 4], [119, -1], [114, -4], [110, -3]],
  newGuinea: [[131, -1], [141, -3], [147, -8], [141, -9], [134, -5]],
  australia: [
    [113, -22], [114, -26], [116, -32], [119, -34], [125, -32], [131, -32], [137, -33],
    [140, -38], [146, -39], [150, -37], [153, -30], [153, -25], [146, -19], [142, -11],
    [136, -12], [131, -11], [129, -15], [125, -17], [122, -18], [114, -21],
  ],
  newZealand: [[173, -35], [176, -38], [178, -38], [175, -41], [172, -41], [170, -44], [167, -46], [170, -46], [174, -41]],
  fiji: [[177, -17], [180, -17], [179, -19], [177, -19]],
}

/** Siluetas listas para pintar, en el orden en que se dibujan. */
export const LANDMASS_PATHS: readonly { id: string; d: string }[] = Object.entries(LANDMASSES).map(
  ([id, ring]) => ({ id, d: toPath(ring) })
)

/**
 * Paralelos y meridianos de fondo. No son información —el mapa no es un
 * instrumento— sino la retícula tenue que hace que se lea como un mapa.
 */
export const GRID_MERIDIANS: readonly number[] = [-150, -120, -90, -60, -30, 0, 30, 60, 90, 120, 150]
export const GRID_PARALLELS: readonly number[] = [60, 30, 0, -30]

/**
 * Ruta como curva suave: una cuadrática por tramo con el punto de control
 * desplazado perpendicularmente, para que el vuelo se lea como un arco y no
 * como una polilínea de GPS.
 */
export function buildRoutePath(points: readonly { lon: number; lat: number }[]): string {
  if (points.length === 0) return ''
  const first = project(points[0].lon, points[0].lat)
  let d = `M${first.x.toFixed(1)},${first.y.toFixed(1)}`

  for (let i = 1; i < points.length; i += 1) {
    const from = project(points[i - 1].lon, points[i - 1].lat)
    const to = project(points[i].lon, points[i].lat)
    const dx = to.x - from.x
    const dy = to.y - from.y
    // El arco se levanta un 10 % de la longitud del tramo, siempre hacia el
    // norte (y negativa), que es como se dibujan las rutas aéreas. El tope de 6
    // unidades es lo que impide que los saltos largos (el despegue desde el
    // Polo Norte, el salto del Atlántico) se conviertan en parábolas gigantes
    // que cruzan el mapa por encima de todo lo demás.
    const lift = Math.min(Math.hypot(dx, dy) * 0.1, 6)
    const cx = (from.x + to.x) / 2
    const cy = (from.y + to.y) / 2 - lift
    d += `Q${cx.toFixed(1)},${cy.toFixed(1)} ${to.x.toFixed(1)},${to.y.toFixed(1)}`
  }

  return d
}

export interface Star {
  /** Porcentaje del ancho del contenedor (0..100). */
  x: number
  /** Porcentaje del alto del contenedor (0..100). */
  y: number
  r: number
  /** Segundos de retardo del parpadeo, para que no titilen a la vez. */
  delay: number
}

/**
 * Estrellas del fondo, en un espacio 0..100 para que la capa de cielo pueda
 * estirarse a todo el contenedor (el mapa se centra dentro, así que arriba y
 * abajo queda cielo que también hay que poblar).
 *
 * La secuencia es **determinista** (no `Math.random`): el servidor y el cliente
 * tienen que pintar exactamente el mismo cielo o React descarta la hidratación.
 */
export function buildStars(count: number): readonly Star[] {
  const stars: Star[] = []
  for (let i = 0; i < count; i += 1) {
    // Secuencia de baja discrepancia (R2): reparte los puntos sin apelotonarlos.
    stars.push({
      x: Number((((i + 1) * 0.7548776662466927 * 100) % 100).toFixed(2)),
      y: Number((((i + 1) * 0.5698402909980532 * 100) % 100).toFixed(2)),
      r: Number((0.08 + ((i * 7) % 5) * 0.035).toFixed(3)),
      delay: Number((((i * 13) % 40) / 10).toFixed(1)),
    })
  }
  return stars
}
